import { useState, useCallback, useEffect, useRef } from "react";
import { DateRangePickerProps } from "@cloudscape-design/components";
import { FailedExecutionsQuery, FailedExecutionsResult, FailureStatus, StateMachineSummary } from "../services/StepFunctionFailuresService";
import { ServiceResult } from "../services";
import { Realm, RealmLower, NotifyFn, SFN_FAILURES_LABELS } from "../constants";
import { DateRangeConverter } from "../utils/DateRangeConverter";

export interface FailuresConfig {
  realm: Realm;
  stateMachineArn: string;
  dateRange: DateRangePickerProps.Value | null;
  includeFailed: boolean;
  includeTimedOut: boolean;
  includeAborted: boolean;
}

export interface SearchedContext {
  stateMachineName: string;
  realm: Realm;
}

export type StateMachinesStatus = "loading" | "error" | "finished";

const DEFAULT_CONFIG: FailuresConfig = {
  realm: "NA",
  stateMachineArn: "",
  dateRange: { type: "relative", amount: 1, unit: "day" },
  includeFailed: true,
  includeTimedOut: false,
  includeAborted: false,
};

export const toStatuses = (config: FailuresConfig): FailureStatus[] => {
  const statuses: FailureStatus[] = [];
  if (config.includeFailed) {
    statuses.push("FAILED");
  }
  if (config.includeTimedOut) {
    statuses.push("TIMED_OUT");
  }
  if (config.includeAborted) {
    statuses.push("ABORTED");
  }
  return statuses;
};

export interface UseStepFunctionFailuresProps {
  onNotify: NotifyFn;
  listStateMachinesFn: (realm: RealmLower) => Promise<ServiceResult<StateMachineSummary[]>>;
  getFailedExecutionsFn: (realm: RealmLower, query: FailedExecutionsQuery) => Promise<ServiceResult<FailedExecutionsResult>>;
}

export interface UseStepFunctionFailuresReturn {
  config: FailuresConfig;
  setConfig: (config: FailuresConfig) => void;
  stateMachines: StateMachineSummary[];
  stateMachinesStatus: StateMachinesStatus;
  reloadStateMachines: () => void;
  loading: boolean;
  result: FailedExecutionsResult | null;
  searchedContext: SearchedContext | null;
  handleSearch: () => Promise<void>;
}

export function useStepFunctionFailures({
  onNotify,
  listStateMachinesFn,
  getFailedExecutionsFn,
}: UseStepFunctionFailuresProps): UseStepFunctionFailuresReturn {
  const [config, setConfig] = useState<FailuresConfig>(DEFAULT_CONFIG);
  const [stateMachines, setStateMachines] = useState<StateMachineSummary[]>([]);
  const [stateMachinesStatus, setStateMachinesStatus] = useState<StateMachinesStatus>("loading");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<FailedExecutionsResult | null>(null);
  const [searchedContext, setSearchedContext] = useState<SearchedContext | null>(null);
  const inFlightRef = useRef(false);
  const listRequestIdRef = useRef(0);
  const listFnRef = useRef(listStateMachinesFn);
  listFnRef.current = listStateMachinesFn;
  const notifyRef = useRef(onNotify);
  notifyRef.current = onNotify;

  const loadStateMachines = useCallback(async (realm: Realm): Promise<void> => {
    const requestId = ++listRequestIdRef.current;
    setStateMachinesStatus("loading");
    setStateMachines([]);
    const response = await listFnRef.current(realm.toLowerCase() as RealmLower);
    if (requestId !== listRequestIdRef.current) {
      return;
    }
    if (response.status === "error") {
      setStateMachinesStatus("error");
      notifyRef.current("error", `${SFN_FAILURES_LABELS.STATE_MACHINES_ERROR}: ${response.message}`);
      return;
    }
    setStateMachines(response.data);
    setStateMachinesStatus("finished");
  }, []);

  useEffect(() => {
    loadStateMachines(config.realm);
  }, [config.realm, loadStateMachines]);

  const reloadStateMachines = useCallback((): void => {
    loadStateMachines(config.realm);
  }, [config.realm, loadStateMachines]);

  const handleSearch = useCallback(async (): Promise<void> => {
    if (inFlightRef.current) {
      return;
    }
    const stateMachine = stateMachines.find((machine) => machine.arn === config.stateMachineArn);
    if (!stateMachine) {
      onNotify("error", SFN_FAILURES_LABELS.STATE_MACHINE_PLACEHOLDER);
      return;
    }
    const statuses = toStatuses(config);
    if (statuses.length === 0) {
      onNotify("error", SFN_FAILURES_LABELS.STATUS_REQUIRED);
      return;
    }
    const range = DateRangeConverter.toDates(config.dateRange);
    if (!range) {
      onNotify("error", SFN_FAILURES_LABELS.INVALID_RANGE);
      return;
    }

    inFlightRef.current = true;
    setLoading(true);
    setResult(null);

    try {
      const response = await getFailedExecutionsFn(config.realm.toLowerCase() as RealmLower, {
        stateMachineArn: config.stateMachineArn,
        startDate: range.startDate,
        endDate: range.endDate,
        statuses,
      });
      if (response.status === "error") {
        onNotify("error", `${SFN_FAILURES_LABELS.SEARCH_FAILED}: ${response.message}`);
        return;
      }
      setResult(response.data);
      setSearchedContext({ stateMachineName: stateMachine.name, realm: config.realm });
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  }, [config, stateMachines, onNotify, getFailedExecutionsFn]);

  return {
    config,
    setConfig,
    stateMachines,
    stateMachinesStatus,
    reloadStateMachines,
    loading,
    result,
    searchedContext,
    handleSearch,
  };
}
